<template>
  <section>
    <collapsable
      :collapsed="collapsed"
      :title="$t('menu.facilities')"
    >
      <section class="facility-list px-1">
        <div v-if="facilities.length && !isLoading">
          <FacilityListItem v-for="(facility, index) in facilities" :key="index"
            ref="facilityListItem"
            class="pb-2"
            :facility="facility"
            @update-facility-schedules="onUpdateFacilitySchedules"
            @update-facility="onUpdateFacility"
            @delete-facility="onDeleteFacility"
          />
        </div>

        <div v-else class="mt-2 ml-3 mb-5">
          <span class="is-bold">{{$t('facilities.empty_list')}}</span>
        </div>

        <div class="d-flex justify-content-end px-3">
          <va-button v-if="hasWritePermissionsToEntity"
            @click.prevent="onCreateFacility">
            {{ $t('facility.create_facility') }}
          </va-button>
        </div>
      </section>
    </collapsable>

    <CreateOrUpdateFacility v-if="isCreatingOrUpdatingFacility"
      :entity="entity"
      :facility="selectedFacilityForUpdate"
      @facility-created="onFacilityCreated"
      @facility-updated="onFacilityUpdated"
      @cancel="cancelCreateOrUpdateFacility"
    />

    <CreateOrUpdateFacilitySchedules v-if="isUpdateFacilitySchedules"
      :entity="entity"
      :facility="selectedFacilityForUpdate"
      :facilitySchedules="selectedFacilitySchedulesForUpdate"
      @facility-schedule-updated="onFacilitySchedulesUpdated"
      @facility-schedule-created="onFacilitySchedulesCreated"
      @facility-created="onFacilityCreated"
      @facility-updated="onFacilityUpdated"
      @cancel="cancelCreateOrUpdateFacilitySchedules"
    />


    <ConfirmationModal
      v-if="isDeletingFacility"
      :title="$t('facilities.delete_facility')"
      :body="$t('facilities.delete_facility_message', {facility: this.selectedFacilityForDeletion.name})"
      @modal-confirmed="confirmDeleteFacility"
      @modal-canceled="cancelDeleteFacility" />

  </section>

</template>

<script>
import { FacilityService, FacilityScheduleService } from '@/api/AuthService'
import Collapsable from '@/components/common/Collapsable'
import FacilityListItem from '@/components/entities/facilities/FacilityListItem'
import CreateOrUpdateFacility from '@/components/entities/facilities/CreateOrUpdateFacility'
import CreateOrUpdateFacilitySchedules from '@/components/entities/facilities/facility-schedules/CreateOrUpdateFacilitySchedules'
import ConfirmationModal from '@/components/common/ConfirmationModal'

const entityInfoType = 110
const writeAccessType = 2

export default {
  props: {
    entity: {
      type: Object,
      required: true
    },
    collapsed: {
      type: Boolean,
      required: false
    }
  },
  components: {
    Collapsable,
    FacilityListItem,
    ConfirmationModal,
    CreateOrUpdateFacility,
    CreateOrUpdateFacilitySchedules
  },
  computed: {
    authService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollauth_server) || null
    },
    hasWritePermissionsToEntity: function () {
      return this.authService && this.authService.user_permissions.some(p => (p.entity_id === null || p.entity_id === this.entity.id) && p.info_type === entityInfoType && p.access_type === writeAccessType)
    },
    authServiceToken: function () {
      return this.$store.state.auth.authSrvToken
    }
    // ...mapState({
    //   'leadStatuses': (state) => state.leadsWeb.leadStatuses,
    //   'leadsWebToken': (state) => state.leadsWeb.leadsWebSrvToken,
    // }),
  },
  data () {
    return {
      isLoading: true,
      isCreatingOrUpdatingFacility: false,
      isUpdateFacilitySchedules: false,
      isDeletingFacility: false,
      selectedFacilityForUpdate: null,
      selectedFacilityForDeletion: null,
      selectedFacilitySchedulesForUpdate: null,
      facilities: []
    }
  },
  methods: {
    loadFacilities () {
      this.isLoading = true
      FacilityService.getFacilitiesByEntity(this.entity.id)
        .then(facilities => {
          this.facilities = facilities
          this.loadFacilitiesSchedules()
        })
        .catch(errors => {
          console.log(errors)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    loadFacilitiesSchedules () {
      this.isLoading = true
      const ids = this.facilities.map( facility => facility.id ) || []
      this.clearFacilitysSchedules()
      FacilityScheduleService.getByFacilitys(
        this.authServiceToken,
        {
          facilities: ids.join(',')
        }
      )
        .then(facilitieSchedules => {
          this.facilitieSchedules = facilitieSchedules

          this.facilitieSchedules.forEach(schedule => {
            const facility = this.facilities.find(facility => facility.id === schedule.facility.id)

            if (facility && facility.schedules) {
              facility.schedules.push(schedule)
            }
          });
        })
        .catch(errors => {
          console.log(errors)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    clearFacilitysSchedules: function(){
      this.facilities.forEach(facility => {
        this.$set(facility,'schedules',[])
      })
    },
    onCreateFacility: function () {
      this.selectedFacilityForUpdate = null
      this.isCreatingOrUpdatingFacility = true
    },
    onUpdateFacility: function ({ facility }) {
      this.selectedFacilityForUpdate = facility
      this.isCreatingOrUpdatingFacility = true
    },
    onDeleteFacility: function ({ facility }) {
      this.selectedFacilityForDeletion = facility
      this.isDeletingFacility = true
    },
    cancelCreateOrUpdateFacility: function () {
      this.selectedFacilityForUpdate = null
      this.isCreatingOrUpdatingFacility = false
    },
    onUpdateFacilitySchedules: function ({ facility, facilitySchedules }) {
      this.selectedFacilityForUpdate = facility
      this.selectedFacilitySchedulesForUpdate = facilitySchedules
      this.isUpdateFacilitySchedules = true
    },
    cancelCreateOrUpdateFacilitySchedules: function () {
      this.isUpdateFacilitySchedules = false
    },
    onFacilityCreated: function () {
      this.loadFacilities()
    },
    onFacilitySchedulesUpdated: function (){
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
      }, 10)
      this.loadFacilities()
    },
    onFacilitySchedulesCreated: function (){
      this.loadFacilitiesSchedules()
    },
    onFacilityUpdated: function () {
      this.loadFacilities()
    },
    onFacilityDeleted: function () {
      this.loadFacilities()
    },
    confirmDeleteFacility: function () {
      FacilityService.delete(this.selectedFacilityForDeletion.id)
        .then(() => {
          this.showToast(this.$t('facilities.facility_deleted'), this.$store.state.successToastOptions)
          this.isDeletingFacility = false
          this.onFacilityDeleted()
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('facilities.errors.something_went_wrong'), this.$store.state.errorToastOptions)
        })
    },
    cancelDeleteFacility: function () {
      this.isDeletingFacility = false
      this.selectedFacilityForDeletion = null
    }
  },
  watch: {
    entity: {
      immediate: true,
      handler: function () {
        this.loadFacilities()
      }
    }
  }
}
</script>

<style lang="scss">

  .facilities-widgets {
    .widget-body {
      padding: 1.2rem;
    }
    b {
      text-decoration: underline;
    }

    .widget {
      //border-top: 0.5rem solid #29c4a9 !important;
      height: calc(100% - 1.875rem) !important;
      -webkit-box-shadow: 0px 4px 70px -18px #a0a0a0;
      box-shadow: 0px 2px 40px -10px #dedede;
    }
  }

  .map-icon {
    color: #29c4a9;
    cursor: pointer;
    font-size: 125%;
  }

</style>
